import { useState, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnalyticsTab } from '@/types/analytics.types';
import { useAnalyticsFilters } from './hooks/useAnalyticsFilters';
import {
  useAnalyticsSummary,
  useAnalyticsFundamentals,
  useAnalyticsHeatmap,
  useAnalyticsOna,
  useAnalyticsGenerational,
  useAnalyticsOmissions,
} from '@/hooks/useAnalytics';
import { USE_MOCK, type AnalyticsFilterParams } from '@/api/client';
import { useOuScope } from '@/hooks/useOuScope';
import { PageSpinner } from '@/components/common/PageSpinner';
import { FilterBar } from './components/FilterBar';
import {
  buildDimensionOptions,
  filterFundamentalsByDimension,
  filterGenerationalByDimension,
  filterHeatmapByDimension,
  filterSummaryByDimension,
  resolveDimensionName,
} from './utils/dimensionFilter';
import { ResumenTab } from './tabs/ResumenTab';
import { FundamentalsTab } from './tabs/FundamentalsTab';
import { HeatmapTab } from './tabs/HeatmapTab';
import { OnaTab } from './tabs/OnaTab';
import { GeneracionalTab } from './tabs/GeneracionalTab';
import './AnalyticsView.css';
import '@/components/common/OuTree/OuTreeCard.css';

const TABS: Array<{ id: AnalyticsTab; icon: string; labelKey: string }> = [
  { id: 'resumen',       icon: 'fa-chart-pie',    labelKey: 'analytics.tabs.summary'       },
  { id: 'fundamentals',  icon: 'fa-layer-group',  labelKey: 'analytics.tabs.fundamentals'  },
  { id: 'heatmap',       icon: 'fa-table-cells',  labelKey: 'analytics.tabs.heatmap'       },
  { id: 'red-org',       icon: 'fa-share-nodes',  labelKey: 'analytics.tabs.orgNetwork'    },
  { id: 'generacional',  icon: 'fa-people-group', labelKey: 'analytics.tabs.generational'  },
];

interface AnalyticsTabPanelProps {
  tabId: AnalyticsTab;
  activeTab: AnalyticsTab;
  isLoading: boolean;
  children: ReactNode;
}

/**
 * Contenedor de una pestaña de analítica: mantiene el panel en el DOM para conservar la
 * animación de cambio de pestaña, pero solo monta su contenido cuando está activa (y un
 * spinner mientras sus consultas cargan).
 */
function AnalyticsTabPanel({ tabId, activeTab, isLoading, children }: AnalyticsTabPanelProps) {
  const isActive = activeTab === tabId;
  return (
    <div
      className={`au-tab-panel${isActive ? ' active' : ''}`}
      role="tabpanel"
      aria-labelledby={`tab-${tabId}`}
    >
      {isActive && (isLoading ? <PageSpinner /> : children)}
    </div>
  );
}

/** Estado para un administrador de OU sin ninguna unidad organizacional asignada. */
function EmptyScopeCard() {
  const { t } = useTranslation();
  return (
    <div className="au-shell">
      <div className="au-card au-empty-scope">
        <i className="fas fa-lock" aria-hidden="true" />
        <p className="au-empty-scope-title">{t('analytics.noScopeTitle')}</p>
        <p className="au-empty-scope-desc">{t('analytics.noScopeDesc')}</p>
      </div>
    </div>
  );
}

export function AnalyticsView() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<AnalyticsTab>('resumen');
  const { filters, setFilter, resetFilters } = useAnalyticsFilters();
  const { isResolving: isResolvingScope, hasEmptyScope, resolveQueryOuIds } = useOuScope();

  // El alcance del usuario y la OU elegida en el filtro se combinan en una sola lista de OUs
  // (cada una expandida con sus descendientes): el AdminOU nunca puede pedir datos fuera de su
  // rama del árbol, y el admin central obtiene el subárbol completo de la OU que seleccione.
  const apiFilters = useMemo<AnalyticsFilterParams>(() => ({
    ouIds: resolveQueryOuIds(filters.ou),
    startDate: filters.dateFrom || undefined,
    endDate: filters.dateTo || undefined,
    language: i18n.language.split('-')[0],
  }), [resolveQueryOuIds, filters.ou, filters.dateFrom, filters.dateTo, i18n.language]);

  // Mientras el alcance no esté resuelto no se consulta nada: una petición sin ouIds devuelve
  // el tenant completo y expondría datos fuera del alcance del administrador.
  const queryOptions = useMemo(
    () => ({ isEnabled: !isResolvingScope && !hasEmptyScope }),
    [isResolvingScope, hasEmptyScope],
  );

  const { data: summaryData,      isLoading: loadingSummary }      = useAnalyticsSummary(apiFilters, queryOptions);
  const { data: fundamentalsData, isLoading: loadingFundamentals } = useAnalyticsFundamentals(apiFilters, queryOptions);
  const { data: heatmapData,      isLoading: loadingHeatmap }      = useAnalyticsHeatmap(apiFilters, queryOptions);
  const { data: onaData,          isLoading: loadingOna }          = useAnalyticsOna(apiFilters, queryOptions);
  const { data: generationalData, isLoading: loadingGenerational } = useAnalyticsGenerational(apiFilters, queryOptions);
  const { data: omissionsData,    isLoading: loadingOmissions }    = useAnalyticsOmissions(apiFilters, queryOptions);

  // En modo real los tabs reciben datos del servidor; en modo mock quedan undefined y cada
  // tab muestra su estado vacío explícito (sin fallback a mock — ver MOCK_FALLBACK.md).
  const apiSummary      = USE_MOCK ? undefined : summaryData;
  const apiFundamentals = USE_MOCK ? undefined : fundamentalsData;
  const apiHeatmap      = USE_MOCK ? undefined : heatmapData;
  const apiOna          = USE_MOCK ? undefined : onaData;
  const apiGenerational = USE_MOCK ? undefined : generationalData;
  const apiOmissions    = USE_MOCK ? undefined : omissionsData;

  // El filtro por comportamiento se resuelve en el cliente: AnalyticsService ya devuelve cada
  // respuesta desglosada por dimensión con los mismos identificadores en las cinco pestañas, así
  // que recortar la carga descargada da el mismo resultado que volver a consultar y evita un
  // viaje al servidor por cada cambio de selección.
  const dimensionGroups = useMemo(
    () => buildDimensionOptions(apiFundamentals),
    [apiFundamentals],
  );
  const activeDimensionName = useMemo(
    () => resolveDimensionName(dimensionGroups, filters.fundFilter),
    [dimensionGroups, filters.fundFilter],
  );

  const filteredSummary = useMemo(
    () => filterSummaryByDimension(apiSummary, filters.fundFilter),
    [apiSummary, filters.fundFilter],
  );
  const filteredFundamentals = useMemo(
    () => filterFundamentalsByDimension(apiFundamentals, filters.fundFilter),
    [apiFundamentals, filters.fundFilter],
  );
  const filteredHeatmap = useMemo(
    () => filterHeatmapByDimension(apiHeatmap, filters.fundFilter),
    [apiHeatmap, filters.fundFilter],
  );
  const filteredGenerational = useMemo(
    () => filterGenerationalByDimension(apiGenerational, filters.fundFilter),
    [apiGenerational, filters.fundFilter],
  );

  if (isResolvingScope) return <div className="au-shell"><PageSpinner /></div>;

  // Administrador de OU sin ninguna OU válida asignada: no hay nada que pueda ver, y mostrar
  // los reportes sin filtro equivaldría a darle el tenant completo.
  if (hasEmptyScope) return <EmptyScopeCard />;

  return (
    <div className="au-shell">
      {/* Filter bar */}
      <FilterBar filters={filters} setFilter={setFilter} resetFilters={resetFilters} apiFundamentals={apiFundamentals} />

      {/* Tab row + search en la misma fila, search fuera del fondo de tabs */}
      <div className="au-tabs-outer">
        <div className="au-tabs-row">
          <div className="au-tabs-group" role="tablist" aria-label={t('analytics.title')}>
            {TABS.map((tab) => (
              <button
                key={tab.id}
                className={`au-tab-btn${activeTab === tab.id ? ' active' : ''}`}
                role="tab"
                aria-selected={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                <i className={`fas ${tab.icon}`} aria-hidden="true" />
                <span>{t(tab.labelKey)}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="au-search-wrap">
          <i className="fas fa-search" aria-hidden="true" />
          <input
            type="text"
            className="au-search-input"
            placeholder={t('common.search') + '…'}
            value={filters.searchQuery}
            onChange={(e) => setFilter('searchQuery', e.target.value)}
            aria-label={t('common.search')}
          />
        </div>
      </div>

      {/* Tab panels */}
      <AnalyticsTabPanel tabId="resumen" activeTab={activeTab} isLoading={loadingSummary}>
        <ResumenTab apiSummary={filteredSummary} activeDimensionName={activeDimensionName} />
      </AnalyticsTabPanel>

      <AnalyticsTabPanel tabId="fundamentals" activeTab={activeTab} isLoading={loadingFundamentals}>
        <FundamentalsTab apiFundamentals={filteredFundamentals} />
      </AnalyticsTabPanel>

      <AnalyticsTabPanel
        tabId="heatmap"
        activeTab={activeTab}
        isLoading={loadingHeatmap || loadingSummary}
      >
        <HeatmapTab
          apiHeatmap={filteredHeatmap}
          apiSummary={filteredSummary}
          activeDimensionName={activeDimensionName}
        />
      </AnalyticsTabPanel>

      <AnalyticsTabPanel
        tabId="red-org"
        activeTab={activeTab}
        isLoading={loadingOna || loadingOmissions || loadingHeatmap || loadingSummary}
      >
        <OnaTab
          apiOna={apiOna}
          apiOmissions={apiOmissions}
          apiSummary={filteredSummary}
          apiHeatmap={filteredHeatmap}
          activeDimensionName={activeDimensionName}
        />
      </AnalyticsTabPanel>

      <AnalyticsTabPanel
        tabId="generacional"
        activeTab={activeTab}
        isLoading={loadingGenerational}
      >
        <GeneracionalTab
          apiGenerational={filteredGenerational}
          filters={apiFilters}
          activeDimensionName={activeDimensionName}
        />
      </AnalyticsTabPanel>
    </div>
  );
}
